import {
  AlimTalkAccountMode,
  AlimTalkTemplate,
  AlimTalkTemplateVariable,
} from "../../../api/services/notification-center.service";

const VARIABLE_PATTERN = /#\{([^{}]+)\}/g;
const AUTOMATIC_VARIABLE_KEYS = new Set([
  "CUSTOMER_NAME",
  "USERNAME",
  "CUSTOMER_PHONE",
  "PHONE_NUM",
  "SITE_NAME",
  "CAMP_NAME",
  "SUBDOMAIN",
  "SITE_SUBDOMAIN",
].flatMap((key) => [key, key.toLowerCase()]));

export const getAlimTalkTemplateName = (template?: AlimTalkTemplate | null): string => (
  template?.template_name || template?.name || ""
);

export const getAlimTalkTemplateContent = (template?: AlimTalkTemplate | null): string => (
  template?.template_content || template?.content || ""
);

export const getAlimTalkTemplateStatus = (template?: AlimTalkTemplate | null): string => (
  template?.template_status || template?.status || ""
).toUpperCase();

export const isApprovedAlimTalkTemplate = (template: AlimTalkTemplate): boolean => (
  !template.is_archived && ["APR", "APPROVED"].includes(getAlimTalkTemplateStatus(template))
);

export const getAlimTalkTemplateVariables = (
  template?: AlimTalkTemplate | null,
): AlimTalkTemplateVariable[] => {
  const declared = Array.isArray(template?.variables) && template.variables.length
    ? template.variables
    : Array.isArray(template?.ordered_variables) ? template.ordered_variables : [];
  const normalized = declared
    .map((variable): AlimTalkTemplateVariable | null => {
      if (typeof variable === "string") {
        const key = variable.trim();
        return key ? { key, required: true } : null;
      }
      const key = variable?.key?.trim();
      return key ? { ...variable, key, required: variable.required !== false } : null;
    })
    .filter((variable): variable is AlimTalkTemplateVariable => Boolean(variable));

  if (normalized.length) {
    const seen = new Set<string>();
    return normalized.filter((variable) => {
      if (seen.has(variable.key)) return false;
      seen.add(variable.key);
      return true;
    });
  }

  return extractAlimTalkVariables(getAlimTalkTemplateContent(template)).map((key) => ({
    key,
    required: true,
  }));
};

export const extractAlimTalkVariables = (content: string): string[] => {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const match of content.matchAll(VARIABLE_PATTERN)) {
    const key = match[1]?.trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    keys.push(key);
  }
  return keys;
};

export const isAutomaticAlimTalkVariable = (
  variable: string | AlimTalkTemplateVariable,
): boolean => {
  if (typeof variable !== "string" && ["recipient", "system"].includes(variable.source || "")) return true;
  const key = typeof variable === "string" ? variable : variable.key;
  return AUTOMATIC_VARIABLE_KEYS.has(key);
};

export const renderAlimTalkTemplate = (
  content: string,
  values: Record<string, string>,
): string => content.replace(VARIABLE_PATTERN, (token, rawKey: string) => {
  const key = rawKey.trim();
  return Object.prototype.hasOwnProperty.call(values, key) && values[key] !== ""
    ? values[key]
    : token;
});

export const getUnresolvedAlimTalkVariables = (
  template: AlimTalkTemplate | null,
  values: Record<string, string>,
): string[] => getAlimTalkTemplateVariables(template)
  .filter((variable) => (
    !isAutomaticAlimTalkVariable(variable)
    && variable.required !== false
    && !values[variable.key]?.trim()
  ))
  .map((variable) => variable.key);

export const normalizeAlimTalkAccountMode = (
  value: unknown,
  usingDefault = false,
): AlimTalkAccountMode => {
  const normalized = String(value || "").toUpperCase();
  if (normalized === "DIRECT") return "DIRECT";
  if (normalized === "HEADQUARTERS") return "HEADQUARTERS";
  return usingDefault ? "HEADQUARTERS" : "DIRECT";
};
